import React from 'react';
import { Settings, Wrench, Monitor, Cpu, HardDrive, Shield, Phone, Mail } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Header from '../components/layout/Header';
import { useCart } from '../contexts/CartContext';

interface Service {
    id: number;
    name: string;
    price: string;
    category: 'diagnosis' | 'assembly' | 'repair' | 'maintenance' | 'software';
    icon: React.ReactNode;
}

const Services: React.FC = () => {
    const { cart } = useCart();
    const services: Service[] = [
        {
            id: 1,
            name: "Диагностика на компютър или компонент",
            price: "€30",
            category: 'diagnosis',
            icon: <Monitor className="h-5 w-5" />
        },
        {
            id: 2,
            name: "Подмяна или добавяне на компонент в конфигурация",
            price: "€20",
            category: 'assembly',
            icon: <Cpu className="h-5 w-5" />
        },
        {
            id: 3,
            name: "Сглобяване на конфигурация с части от магазина",
            price: "€30",
            category: 'assembly',
            icon: <Settings className="h-5 w-5" />
        },
        {
            id: 4,
            name: "Смяна на кондензатор или друг елемент от дънна платка",
            price: "по договаряне",
            category: 'repair',
            icon: <Wrench className="h-5 w-5" />
        },
        {
            id: 5,
            name: "Сглобяване на конфигурация с части на клиента",
            price: "€40",
            category: 'assembly',
            icon: <Settings className="h-5 w-5" />
        },
        {
            id: 6,
            name: "Прехвърляне и асемблиране на конфигурация в нова кутия",
            price: "€40",
            category: 'assembly',
            icon: <Settings className="h-5 w-5" />
        },
        {
            id: 7,
            name: "Смяна на термо паста на процесор с включена цена на термо паста",
            price: "€20",
            category: 'maintenance',
            icon: <Cpu className="h-5 w-5" />
        },
        {
            id: 8,
            name: "Смяна на термо паста и термо подложки на видеокарта",
            price: "€40-€80",
            category: 'maintenance',
            icon: <Monitor className="h-5 w-5" />
        },
        {
            id: 9,
            name: "Обновяване на BIOS на дънна платка",
            price: "€15",
            category: 'software',
            icon: <Settings className="h-5 w-5" />
        },
        {
            id: 10,
            name: "Инсталиране на драйвъри",
            price: "€15",
            category: 'software',
            icon: <Settings className="h-5 w-5" />
        },
        {
            id: 11,
            name: "Ремонт на компютър или компонент",
            price: "по договаряне",
            category: 'repair',
            icon: <Wrench className="h-5 w-5" />
        },
        {
            id: 12,
            name: "Почистване и профилактика на компютър или лаптоп",
            price: "€40-€50",
            category: 'maintenance',
            icon: <Shield className="h-5 w-5" />
        },
        {
            id: 13,
            name: "Инсталация на Windows и драйвъри",
            price: "€35",
            category: 'software',
            icon: <Monitor className="h-5 w-5" />
        },
        {
            id: 14,
            name: "Почистване от вируси и премахване на зловреден софтуер",
            price: "€30",
            category: 'software',
            icon: <Shield className="h-5 w-5" />
        },
        {
            id: 15,
            name: "Смяна или добавяне на диск или памет на лаптоп",
            price: "€20",
            category: 'assembly',
            icon: <HardDrive className="h-5 w-5" />
        },
        {
            id: 16,
            name: "Смяна или добавяне на компютърен компонент",
            price: "€20",
            category: 'assembly',
            icon: <Cpu className="h-5 w-5" />
        },
        {
            id: 17,
            name: '"Cable Management" - Подреждане и организация на кабелите в конфигурация',
            price: "€25",
            category: 'assembly',
            icon: <Settings className="h-5 w-5" />
        },
        {
            id: 18,
            name: "Копиране, клониране и запазване на информация от HDD/SSD/NVMe",
            price: "€50",
            category: 'software',
            icon: <HardDrive className="h-5 w-5" />
        }
    ];

    const categoryNames = {
        diagnosis: 'Диагностика',
        assembly: 'Сглобяване и монтаж',
        repair: 'Ремонт',
        maintenance: 'Поддръжка',
        software: 'Софтуер'
    };

    const categoryColors = {
        diagnosis: 'bg-blue-50 border-blue-200',
        assembly: 'bg-green-50 border-green-200',
        repair: 'bg-red-50 border-red-200',
        maintenance: 'bg-yellow-50 border-yellow-200',
        software: 'bg-purple-50 border-purple-200'
    };

    const getCategoryServices = (category: keyof typeof categoryNames) => {
        return services.filter(service => service.category === category);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Header cartItemsCount={cart.items.length} cartTotal={cart.total} />
            <Navigation />
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        Наши услуги
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Предлагаме широка гама от професионални услуги за компютри и компоненти.
                    </p>
                </div>

                {/* Services by Category */}
                <div className="space-y-12">
                    {Object.entries(categoryNames).map(([categoryKey, categoryName]) => {
                        const categoryServices = getCategoryServices(categoryKey as keyof typeof categoryNames);
                        if (categoryServices.length === 0) return null;

                        return (
                            <div key={categoryKey} className="bg-white rounded-xl shadow-lg overflow-hidden">
                                <div className={`p-6 ${categoryColors[categoryKey as keyof typeof categoryColors]}`}>
                                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                        <Settings className="mr-3 h-6 w-6" />
                                        {categoryName}
                                    </h2>
                                </div>

                                <div className="p-6">
                                    <div className="grid gap-4">
                                        {categoryServices.map((service) => (
                                            <div
                                                key={service.id}
                                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                            >
                                                <div className="flex items-center flex-1">
                                                    <div className="text-primary-600 mr-4">
                                                        {service.icon}
                                                    </div>
                                                    <span className="text-gray-800 font-medium">
                                                        {service.name}
                                                    </span>
                                                </div>

                                                <div className="flex flex-col items-end ml-4">
                                                    <span className="text-lg font-bold text-primary-600">
                                                        {service.price}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Contact Information */}
                <div className="mt-16 bg-primary-600 text-white rounded-xl p-8">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold mb-4">
                            Имате нужда от консултация?
                        </h2>
                        <p className="text-lg mb-6 opacity-90">
                            Свържете се с нас
                        </p>

                        <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
                            <div className="flex items-center">
                                <Phone className="mr-2 h-5 w-5" />
                                <span className="text-lg">+359 877 769 575</span>
                            </div>

                            <div className="flex items-center">
                                <Mail className="mr-2 h-5 w-5" />
                                <span className="text-lg">info@pccenter.bg</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Information */}
                <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">
                        Важна информация
                    </h3>

                    <div className="grid md:grid-cols-2 gap-6 text-gray-600">
                        <div>
                            <h4 className="font-semibold text-gray-800 mb-2">Гаранция</h4>
                            <p>Всички наши услуги се извършват с гаранция. Гаранционният срок варира в зависимост от вида на услугата.</p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800 mb-2">Време за изпълнение</h4>
                            <p>Повечето услуги се изпълняват в рамките на 1-2 работни дни. За по-сложни ремонти времето може да бъде удължено.</p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800 mb-2">Цени</h4>
                            <p>Всички цени са крайни и включват работа. Цените на резервни части не са включени, освен ако не е посочено друго.</p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800 mb-2">Диагностика</h4>
                            <p>Ако изберете да извършите ремонт при нас, цената на диагностиката се приспада от общата сума.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Services;
